import React from 'react';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { AssetTreeView } from '../../../types/app/assetTreeView.types';
import { utcFormat } from '../../../types/core/date.types';
import * as machineDataApi from '../../../api/app/machineDataApi';
import { Loader } from '../../../components/core/Loader/Loader';
import { generateParentNames } from '../../../utils/core/functions/assetTreeView.fuctions';
import { ChartTypes, ChartDataSet, Color } from '../../../types/app/chart.types';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from '../../../contexts/translation';
import { useStyles } from './DashboardStateHistory.styles';
import { ChartsStateContainer } from '../../Charts/ChartsStateContainer';
import { ChartsStateQtyContainer } from '../../Charts/ChartsStateQtyContainer';
import { ChartsStateTimeContainer } from '../../Charts/ChartsStateTimeContainer';
import { useLocalTheme } from '../../../hooks/useLocalTheme';
interface Props {
  dateFrom: Moment;
  dateTo: Moment;
  assetTreeViewData: AssetTreeView;
  selectedAssets: AssetTreeView[];
  ignoredStates: string[];
}
interface ValueQty {
  value: number;
  qty: number;
}
interface ValueTime {
  value: number;
  finalTime: number;
}

export function DashboardStateHistoryDataLoader({
  dateFrom,
  dateTo,
  assetTreeViewData,
  selectedAssets,
  ignoredStates,
}: Props) {
  const { localTheme } = useLocalTheme();
  const { t } = useTranslation();
  const classes = useStyles();
  const [assetTreeViewDataCustom, setAssetTreeViewDataCustom] = useState<AssetTreeView | null>();
  const [state, setState] = useState<any>(null);
  const [proccessedData, setProccessedData] = useState<any>(null);
  const [labels, setLabels] = useState<any>(null);
  const [colorArr, setColorArr] = useState<Color[]>();
  const [isLoading, setLoading] = useState<boolean>(false);

  // Rekurzivně doplníme parent names do stromu assetů
  useEffect(() => {
    if (!assetTreeViewData) return;
    setAssetTreeViewDataCustom(generateParentNames(assetTreeViewData, ''));
  }, [assetTreeViewData]);

  // Při změně filtru (assetu nebo datumu), se projde celé asset tree view, a pro každý asset se načtou datasety
  useEffect(() => {
    if (!selectedAssets || !dateFrom || !dateTo) return;
    setLoading(true);
    const getAndSetStatesForAssetTreeView = async () => {
      const resultsStates = await Promise.all(
        selectedAssets.map(async (asset) => {
          let state = await machineDataApi.readStateFilterByDate({
            machine: asset.influxMachineName,
            dateFrom: dateFrom.clone().format(utcFormat),
            dateTo: dateTo.clone().format(utcFormat),
          });
          state = { ...state, id: asset.id };
          return state;
        }),
      );
      setState(resultsStates);
    };
    getAndSetStatesForAssetTreeView();
  }, [selectedAssets, dateFrom, dateTo, ignoredStates]);

  /**
   * Generate colors for each dataset
   */
  const generateColors = (datasetNum: number) => {
    let colors: Color[] = [];
    for (let i = 0; i < datasetNum; i++) {
      colors.push({
        r: Math.floor(Math.random() * 255),
        g: Math.floor(Math.random() * 255),
        b: Math.floor(Math.random() * 255),
      });
    }
    setColorArr(colors);
  };

  /**
   * Set colors, labels, processed data for every selected asset
   */
  useEffect(() => {
    if (!state || state?.data?.length === 0) return setProccessedData(null);
    let data: any[] = [];
    let _labels: any[] = [];
    let datasetNum = 0;
    state.forEach((st: any) => {
      st.data.forEach((dataSet: any) => {
        datasetNum++;
        for (let index = 0; index < dataSet.values.length; index++) {
          const item = dataSet.values[index];
          data.push({
            dataId: dataSet.dataId + '_' + st.id,
            time: item.time,
            value: item.value,
          });
          _labels.push(item.time);
        }
      });
    });
    generateColors(datasetNum);
    const sortedLabels = _labels.sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
    const sortedData = data.sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf());
    setLabels(sortedLabels);
    setProccessedData(sortedData);
    setLoading(false);
  }, [state]);

  const getDataForStateChart = (dataset: any, asset: any) => {
    if (!proccessedData) return;
    let dataArr: any = [];
    for (let i = 0; i < proccessedData.length; i++) {
      if (proccessedData[i].dataId === dataset.dataId + '_' + asset.id) {
        dataArr.push(proccessedData[i].value);
      } else dataArr.push(null);
    }
    return dataArr;
  };

  const dashboardChartStates = useMemo(() => {
    if (isLoading || !proccessedData || !state || state.length === 0 || !colorArr)
      return <Loader />;
    // Graf states
    let chartsDataStates: any[] = [];
    let chartsDataQty: any[] = [];
    let chartsDataTime: any[] = [];
    let chartsDataStatesSorted: ChartDataSet[] = [];
    let colorIndex = -1;
    for (let index = 0; index < state.length; index++) {
      const asset = state[index];
      let _asset = selectedAssets.find((a: any) => a.id === asset.id);
      for (let index = 0; index < asset.data.length; index++) {
        const dataset = asset.data[index];
        let _dataset = _asset?.states.find((s: any) => s.influxValueName === dataset.dataId);
        colorIndex++;
        // skip dataset if every value is in ignore array
        // show states chart only if dateFrom and dateTo is same day
        if (
          dataset.values.some((x: any) => !ignoredStates.includes(x.value.toString())) &&
          dateTo.diff(dateFrom, 'day') < 1
        ) {
          let tmpDataset = {
            dataSetId: dataset.dataId,
            chartType: ChartTypes.LINE,
            label: _dataset ? `${_dataset?.parentAssetName} - ${_dataset?.name}` : '',
            data: getDataForStateChart(dataset, asset),
            colors: [
              { r: colorArr[colorIndex].r, g: colorArr[colorIndex].g, b: colorArr[colorIndex].b },
            ],
          };
          chartsDataStates.push(tmpDataset);
        }
        let _valuesQty: ValueQty[] = [];
        let _valuesTime: ValueTime[] = [];
        let deltaTime: number = 0;

        for (let index = 0; index < dataset.values.length; index++) {
          if (dataset.values.length === 0) return;
          const item = dataset.values[index];
          // not in ignored arr
          if (ignoredStates.findIndex((st: any) => Number(st) === item.value) === -1) {
            // is in _values array?
            if (_valuesQty.some((v: any) => v.value === item.value)) {
              // yes --> qty + 1
              let i = _valuesQty.findIndex((v: any) => v.value === item.value);
              _valuesQty[i] = { value: item.value, qty: _valuesQty[i].qty + 1 };
            } else {
              // no --> add
              _valuesQty = [..._valuesQty, { value: item.value, qty: 1 }];
            }
          }
          ///////////////////// TIME ///////////////////////////
          // existuje už hodnota ve _values?
          if (_valuesTime.some((v: any) => v.value === item.value)) {
            // delta současnýho a předchozího itemu
            deltaTime = deltaTime + moment(item.time).diff(moment(dataset.values[index - 1].time));
            // byla předchozí item.value stejná?
            let previousIndex = _valuesTime.findIndex(
              (v: any) => v.value === dataset.values[index - 1].value,
            );
            if (dataset.values[index - 1].value === item.value) {
              // ano --> přičíst k finalTime
              _valuesTime[previousIndex].finalTime =
                _valuesTime[previousIndex].finalTime +
                moment(item.time).diff(moment(dataset.values[index - 1].time));
            } else {
              // ne --> přičíst deltu k finalu a reset delta time
              // najít index předchozí hodnoty ve _valuesTime
              _valuesTime[previousIndex].finalTime =
                _valuesTime[previousIndex].finalTime + deltaTime;
              deltaTime = 0;
            }
          } else {
            if (index > 0) {
              deltaTime =
                deltaTime + moment(item.time).diff(moment(dataset.values[index - 1].time));
              _valuesTime[_valuesTime.length - 1].finalTime =
                _valuesTime[_valuesTime.length - 1].finalTime + deltaTime;
            }
            // neexistuje -> přidám do _valuesTime
            _valuesTime = [..._valuesTime, { value: item.value, finalTime: 0 }];
            deltaTime = 0;
          }
        }
        // values QTY
        _valuesQty.forEach((val: any) => {
          let tmpLabel = `${_dataset?.parentAssetName} - ${_dataset?.name} - ${val?.value}`;
          if (chartsDataQty.some((item: any) => item.label === tmpLabel)) {
            tmpLabel = `${_dataset?.parentAssetName} - ${_dataset?.name} - ${val?.value}_${asset.id}`;
          }
          chartsDataQty.push({
            dataSetId: dataset.dataId,
            chartType: ChartTypes.BAR,
            label: tmpLabel,
            data: val.qty,
            colors: [
              {
                r: colorArr[colorIndex].r,
                g: colorArr[colorIndex].g,
                b: colorArr[colorIndex].b,
              },
            ],
          });
        });
        // values Time
        _valuesTime.forEach((val: any) => {
          if (ignoredStates.findIndex((st: any) => Number(st) === val.value) === -1) {
            let tmpLabel = `${_dataset?.parentAssetName} - ${_dataset?.name} - ${val?.value}`;
            if (chartsDataTime.some((item: any) => item.label === tmpLabel)) {
              tmpLabel = `${_dataset?.parentAssetName} - ${_dataset?.name} - ${val?.value}_${asset.id}`;
            }
            let tmpDataset = {
              dataSetId: dataset.dataId,
              chartType: ChartTypes.BAR,
              label: tmpLabel,
              data: val.finalTime,
              colors: [
                {
                  r: colorArr[colorIndex].r,
                  g: colorArr[colorIndex].g,
                  b: colorArr[colorIndex].b,
                },
              ],
            };
            chartsDataTime.push(tmpDataset);
          }
        });
      }
    }

    const chartsDataStatesSets: ChartDataSet[] =
      chartsDataStates &&
      chartsDataStates.map((item) => {
        return {
          chartType: ChartTypes.LINE,
          style: {
            backgroundColor: {
              r: item.colors[0].r,
              g: item.colors[0].g,
              b: item.colors[0].b,
            },
            borderColor:
              'rgb(' + item.colors[0].r + ',' + item.colors[0].g + ',' + item.colors[0].b + ')',
            borderWidth: 1,
            fill: false,
          },
          label: item.label,
          colors: item.colors,
          x: labels,
          y: item.data,
        };
      });

    // kdyz je 0 nebo jedna ne-null hodnoda, vyhodit dataset z pole
    chartsDataStates &&
      chartsDataStatesSets.map((dataset: ChartDataSet) => {
        let yValues = dataset.y.filter((val: any) => val !== null);
        if (yValues.length > 1) chartsDataStatesSorted = [...chartsDataStatesSorted, dataset];
      });

    // Sort QTY data
    let sortedChartsDataQty = chartsDataQty.sort((a, b) => b.data - a.data);
    let sortedLabelsQty: string[] = [];
    sortedChartsDataQty.map((dataset: any) => {
      sortedLabelsQty = [...sortedLabelsQty, dataset.label];
    });

    const chartsDataQtySets: ChartDataSet[] = sortedChartsDataQty.map((item) => {
      return {
        chartType: ChartTypes.BAR,
        style: {
          backgroundColor: {
            r: item.colors[0].r,
            g: item.colors[0].g,
            b: item.colors[0].b,
          },
          borderColor:
            'rgb(' + item.colors[0].r + ',' + item.colors[0].g + ',' + item.colors[0].b + ')',
          borderWidth: 1,
          fill: false,
        },
        label: item.label,
        colors: item.colors,
        x: item.data,
        y: sortedLabelsQty.map((label: any) => {
          if (item.label === label) return item.data;
          return null;
        }),
      };
    });

    // Sort time data
    let _sortedChartsDataTime = chartsDataTime.sort((a, b) => b.data - a.data);
    let sortedChartsDataTime: any[] = [];
    let sortedLabelsTime: string[] = [];
    _sortedChartsDataTime.map((dataset: any) => {
      if (dataset.data > 0) {
        sortedChartsDataTime = [...sortedChartsDataTime, dataset];
        sortedLabelsTime = [...sortedLabelsTime, dataset.label];
      }
    });

    const chartsDataTimeSets: ChartDataSet[] = sortedChartsDataTime.map((item) => {
      return {
        chartType: ChartTypes.BAR,
        style: {
          backgroundColor: {
            r: item.colors[0].r,
            g: item.colors[0].g,
            b: item.colors[0].b,
          },
          borderColor:
            'rgb(' + item.colors[0].r + ',' + item.colors[0].g + ',' + item.colors[0].b + ')',
          borderWidth: 1,
          fill: false,
        },
        label: item.label,
        colors: item.colors,
        x: item.data,
        y: sortedLabelsTime.map((label: any) => {
          if (item.label === label) {
            return item.data;
          } else {
            return null;
          }
        }),
      };
    });
    console.timeEnd('01');
    return (
      <>
        {chartsDataQtySets.length === 0 || chartsDataTimeSets.length === 0 ? (
          <Typography variant={'h5'}>{t('general.messages.error.no-data-to-display')}</Typography>
        ) : (
          <>
            {chartsDataStatesSorted && chartsDataStatesSorted.length > 0 && (
              <ChartsStateContainer
                chartData={chartsDataStatesSorted}
                labels={labels}
                chartTitle={t('page.dashboard.history.state.chart-title-1')}
                localTheme={localTheme}
              />
            )}
            <ChartsStateQtyContainer
              chartData={chartsDataQtySets}
              labels={sortedLabelsQty}
              chartTitle={t('page.dashboard.history.state.chart-title-2')}
              localTheme={localTheme}
            />
            <ChartsStateTimeContainer
              chartData={chartsDataTimeSets}
              labels={sortedLabelsTime}
              chartTitle={t('page.dashboard.history.state.chart-title-3')}
              localTheme={localTheme}
            />
          </>
        )}
      </>
    );
  }, [proccessedData, isLoading, selectedAssets]);

  if (!assetTreeViewDataCustom || !state) return <Loader />;

  if (selectedAssets.length === 0)
    return <Typography variant={'h5'}>{t('page.dashboard.history.dataset-no-asset')}</Typography>;

  return (
    <Grid container>
      <Grid item xs={12} mt={2}>
        <Typography variant={'h5'} className={classes.datasetHeadline}>
          {t('page.dashboard.history.state.dataset-headline-1')}
        </Typography>
      </Grid>
      {dashboardChartStates}
    </Grid>
  );
}
